// Global auth store — a thin, framework-free wrapper over pb.authStore.
//
// Design principle: the app is anonymous-first. Nothing here forces a login.
// Views subscribe to auth changes and re-render; guests simply get a
// read-only experience with sign-in CTAs in place of personal features.
import { pb } from "./pocketbase.js";

const listeners = new Set();

function notify() {
  const snapshot = getState();
  for (const fn of listeners) {
    try {
      fn(snapshot);
    } catch (err) {
      console.error("auth listener error", err);
    }
  }
}

// Re-broadcast PocketBase's own auth changes (login, logout, token refresh).
pb.authStore.onChange(() => notify());

export function getState() {
  return {
    isAuthed: pb.authStore.isValid,
    user: pb.authStore.record || null,
    userId: pb.authStore.record?.id || null,
  };
}

export function isAuthed() {
  return pb.authStore.isValid;
}

export function currentUser() {
  return pb.authStore.record || null;
}

// Subscribe to auth-state changes. Returns an unsubscribe function.
export function subscribe(fn) {
  listeners.add(fn);
  fn(getState());
  return () => listeners.delete(fn);
}

export async function login(email, password) {
  return pb.collection("users").authWithPassword(email, password);
}

export async function signup({ email, password, passwordConfirm, name }) {
  const data = {
    email,
    password,
    passwordConfirm: passwordConfirm ?? password,
    name: name || email.split("@")[0],
  };
  const record = await pb.collection("users").create(data);
  // Immediately sign the new user in.
  await login(email, password);
  // Best-effort: create their learning profile (ignored if rules/collection
  // are not present yet).
  try {
    await pb.collection("user_profiles").create({
      user_id: record.id,
      current_learning_tier: "A1",
    });
  } catch {
    /* profile creation is non-critical for the foundation slice */
  }
  return record;
}

export function logout() {
  pb.authStore.clear();
}

// Ask PocketBase to email a password-reset link. Resolves regardless of whether
// the email exists (PocketBase doesn't reveal that — anti-enumeration). The
// email only actually sends if SMTP is configured on the PocketBase server.
export async function requestPasswordReset(email) {
  return pb.collection("users").requestPasswordReset(email);
}

// Complete a reset using the token from the emailed link + the new password.
export async function confirmPasswordReset(token, password, passwordConfirm) {
  return pb
    .collection("users")
    .confirmPasswordReset(token, password, passwordConfirm ?? password);
}

// Optional OAuth hook (e.g. Google) — wired but unused until providers are
// configured in PocketBase.
export async function loginWithProvider(provider) {
  return pb.collection("users").authWithOAuth2({ provider });
}
